import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Car,
  MapPin,
  Clock,
  Calendar,
  Users,
  DollarSign,
  MessageSquare,
  Send,
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  User,
  Phone,
  Shield,
  AlertTriangle
} from 'lucide-react';

const contributionLabels = {
  fuel: 'Fuel Contribution',
  fixed: 'Fixed Amount',
  negotiable: 'Negotiable',
  free: 'Free Ride'
};

const CarpoolRoutePage = () => {
  const { routeId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [messages, setMessages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isRider, setIsRider] = useState(false);
  const [myRequest, setMyRequest] = useState(null);
  const messagesEndRef = useRef(null);

  const [joinForm, setJoinForm] = useState({
    pickup_location: '',
    message: '',
    agreed_to_contribution: false
  });

  useEffect(() => {
    fetchRoute();
  }, [routeId]);

  useEffect(() => {
    if (route && user) {
      const rider = route.riders?.find(r => r.rider_id === user.id);
      setIsRider(!!rider);
      if (isRider || route.driver_id === user.id) {
        fetchMessages();
      }
    }
  }, [route, user]);

  const fetchRoute = async () => {
    try {
      const response = await axios.get(`${API}/carpool/routes/${routeId}`);
      setRoute(response.data);
      
      // Check if user has a pending request
      if (user && token) {
        try {
          const reqRes = await axios.get(`${API}/carpool/my-ride-requests`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const req = reqRes.data.find(r => r.route_id === routeId);
          setMyRequest(req);
        } catch (e) {}
      }
    } catch (error) {
      toast.error('Route not found');
      navigate('/carpool');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/carpool/routes/${routeId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      // Not authorized or no messages
    }
  };

  const handleJoinRequest = async () => {
    if (!joinForm.pickup_location) {
      toast.error('Please enter your pickup location');
      return;
    }
    if (route.contribution_type !== 'free' && !joinForm.agreed_to_contribution) {
      toast.error('You must agree to the contribution requirement');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/carpool/requests`, {
        route_id: routeId,
        pickup_location: joinForm.pickup_location,
        message: joinForm.message,
        agreed_to_contribution: joinForm.agreed_to_contribution
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request sent to driver!');
      setShowJoinDialog(false);
      fetchRoute();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/carpool/routes/${routeId}/messages`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!route) return null;

  const spotsLeft = route.max_riders - (route.riders?.length || 0);
  const isDriver = user?.id === route.driver_id;
  const canChat = isDriver || isRider;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/carpool')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Carpool
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                <Badge className="bg-white/20 text-white mb-3">{route.route_type}</Badge>
                <h1 className="font-heading text-2xl font-bold">{route.route_name}</h1>
                <div className="flex items-center gap-4 mt-3 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" /> {spotsLeft} spots left
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {route.departure_time}
                  </span>
                </div>
              </div>
              
              <CardContent className="p-6">
                {/* Route Details */}
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">From</p>
                      <p className="font-medium">{route.start_location}</p>
                    </div>
                  </div>
                  
                  {route.stops?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-dashed border-slate-200">
                      <p className="text-sm text-slate-500 mb-2">Pickup Stops</p>
                      {route.stops.map((stop, idx) => (
                        <p key={`${route.id}-stop-${idx}`} className="text-sm text-slate-600">• {stop}</p>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">To</p>
                      <p className="font-medium">{route.end_location}</p>
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-slate-600" />
                    <span className="font-medium">Schedule</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                      <Badge
                        key={day}
                        variant={route.schedule_days?.includes(day) ? 'default' : 'outline'}
                        className={route.schedule_days?.includes(day) ? 'bg-emerald-500' : 'text-slate-400'}
                      >
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </Badge>
                    ))}
                  </div>
                  {route.return_time && (
                    <p className="text-sm text-slate-600 mt-3">Return: {route.return_time}</p>
                  )}
                </div>

                {/* Contribution */}
                <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                    <span className="font-medium">{contributionLabels[route.contribution_type]}</span>
                  </div>
                  {route.contribution_amount && (
                    <p className="text-amber-800 font-medium">{route.contribution_amount}</p>
                  )}
                  {route.contribution_details && (
                    <p className="text-sm text-amber-700 mt-1">{route.contribution_details}</p>
                  )}
                </div>

                {/* Rules */}
                {route.rules && (
                  <div className="mt-6">
                    <h3 className="font-medium mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Driver's Rules
                    </h3>
                    <p className="text-slate-600 text-sm">{route.rules}</p>
                  </div>
                )}

                {/* Current Riders */}
                {route.riders?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-medium mb-3">Current Riders ({route.riders.length})</h3>
                    <div className="flex -space-x-2">
                      {route.riders.map((rider) => (
                        <div
                          key={rider.user?.id || rider.user_id}
                          className="w-10 h-10 bg-plp-gold rounded-full border-2 border-white flex items-center justify-center"
                          title={rider.user?.name}
                        >
                          <span className="text-sm font-bold text-plp-blue">
                            {rider.user?.name?.charAt(0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat (only for participants) */}
            {canChat && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-emerald-500" /> Carpool Chat
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64 border rounded-lg p-4 mb-4">
                    {messages.length === 0 ? (
                      <p className="text-center text-slate-400 py-8">No messages yet. Start the conversation!</p>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg) => (
                          <div
                            key={msg.id || msg.created_at}
                            className={`flex gap-3 ${msg.sender_id === user?.id ? 'flex-row-reverse' : ''}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-plp-gold flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-plp-blue">
                                {msg.sender?.name?.charAt(0)}
                              </span>
                            </div>
                            <div className={`max-w-[70%] p-3 rounded-xl ${
                              msg.sender_id === user?.id
                                ? 'bg-emerald-500 text-white'
                                : 'bg-slate-100'
                            }`}>
                              <p className="text-xs opacity-70 mb-1">{msg.sender?.name}</p>
                              <p className="text-sm">{msg.content}</p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                    />
                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Driver Card */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Driver</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-plp-gold rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-plp-blue">
                      {route.driver?.name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading font-semibold">{route.driver?.name}</p>
                    <p className="text-sm text-slate-500">
                      {route.driver_profile?.vehicle_make} {route.driver_profile?.vehicle_model}
                    </p>
                    <p className="text-xs text-slate-400">
                      {route.driver_profile?.vehicle_color} • {route.driver_profile?.vehicle_year}
                    </p>
                  </div>
                </div>
                {route.driver_profile?.bio && (
                  <p className="text-sm text-slate-600 mt-4">{route.driver_profile.bio}</p>
                )}
              </CardContent>
            </Card>

            {/* Join CTA */}
            {user ? (
              isDriver ? (
                <Link to="/carpool/my-routes">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Car className="w-4 h-4 mr-2" /> Manage This Route
                  </Button>
                </Link>
              ) : isRider ? (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="font-medium text-emerald-800">You're a rider!</p>
                    <p className="text-sm text-emerald-600">Use the chat to coordinate</p>
                  </CardContent>
                </Card>
              ) : myRequest ? (
                <Card className={myRequest.status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}>
                  <CardContent className="p-4 text-center">
                    {myRequest.status === 'pending' ? (
                      <>
                        <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="font-medium text-amber-800">Request Pending</p>
                        <p className="text-sm text-amber-600">Waiting for driver approval</p>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                        <p className="font-medium text-red-800">Request {myRequest.status}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : spotsLeft > 0 ? (
                <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 py-6" data-testid="join-carpool-btn">
                      <Users className="w-4 h-4 mr-2" /> Request to Join
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Join This Carpool</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Your Pickup Location *</Label>
                        <Input
                          value={joinForm.pickup_location}
                          onChange={(e) => setJoinForm(p => ({ ...p, pickup_location: e.target.value }))}
                          placeholder="Where should the driver pick you up?"
                          data-testid="pickup-location"
                        />
                      </div>
                      <div>
                        <Label>Message to Driver (Optional)</Label>
                        <Textarea
                          value={joinForm.message}
                          onChange={(e) => setJoinForm(p => ({ ...p, message: e.target.value }))}
                          placeholder="Introduce yourself or add any notes..."
                          rows={3}
                        />
                      </div>
                      
                      {route.contribution_type !== 'free' && (
                        <div className="p-4 bg-amber-50 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="contribution"
                              checked={joinForm.agreed_to_contribution}
                              onCheckedChange={(c) => setJoinForm(p => ({ ...p, agreed_to_contribution: c }))}
                            />
                            <label htmlFor="contribution" className="text-sm">
                              I agree to the contribution requirement: 
                              <span className="font-medium block">{contributionLabels[route.contribution_type]}</span>
                              {route.contribution_amount && <span className="text-amber-700">{route.contribution_amount}</span>}
                            </label>
                          </div>
                        </div>
                      )}
                      
                      <Button
                        onClick={handleJoinRequest}
                        disabled={submitting}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="submit-join-request"
                      >
                        {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Request
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="font-medium text-red-800">Route Full</p>
                    <p className="text-sm text-red-600">No spots available</p>
                  </CardContent>
                </Card>
              )
            ) : (
              <Link to="/auth">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 py-6">
                  Sign In to Join
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarpoolRoutePage;
